/**
 * What the model layer is doing, and what it is costing.
 *
 * Three questions, in the order an operator asks them:
 *
 * 1. **Is it running at all?** `disabled` and `no_client` are different
 *    failures — one is a config decision, the other is a missing key — and
 *    both present as "the chart has no annotation", which is also what a quiet
 *    market looks like.
 * 2. **What is it costing, against the cap?** ADR-0006's whole argument is that
 *    AI cost is a function of the symbol universe rather than the user count.
 *    The universe shown here is the *demand-gated* set actually being analysed,
 *    not the declared list — reporting the declared one would describe a set
 *    the worker no longer uses.
 * 3. **Is the output getting worse?** `rejectionRate` is the monitored number.
 *    A climb in it is a prompt regression: the model is producing annotations
 *    the validation layer is throwing away, and the user sees fewer of them
 *    without anything erroring.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api, ApiError, fmtUsd, type AiStatusResponse } from '../../lib/api.js';

const LAYER_TONE: Record<string, string> = {
  active: 'chip-ok',
  no_client: 'chip-caution',
  disabled: '',
};

const LAYER_SAYS: Record<string, string> = {
  active: 'Running.',
  no_client: 'Enabled, but there is no API key — Layer 1 only.',
  disabled: 'Switched off in config — Layer 1 only.',
};

const pct = (n: number): string => `${(n * 100).toFixed(1)}%`;

export function AiStatusScreen(): JSX.Element {
  const [data, setData] = useState<AiStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.aiStatus());
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404
        ? 'This is not available on your account.'
        : 'Could not load AI status.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!data) {
    return (
      <div className="scroll-area">
        <h2>AI status</h2>
        <p className="dim">{error ?? 'Loading…'}</p>
      </div>
    );
  }

  const { budget, audit } = data;
  // Over 80% of the month's cap with time left in the period is the moment to
  // know, not the moment it stops.
  const budgetTone = budget.fraction >= 0.8 ? 'chip-breached'
    : budget.fraction >= 0.5 ? 'chip-caution' : 'chip-ok';

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>AI status</h2>
        <span className={`chip ${LAYER_TONE[data.modelLayer] ?? ''}`}>{data.modelLayer}</span>
      </div>
      <p className="panel-body">{LAYER_SAYS[data.modelLayer] ?? ''}</p>

      <section className="admin-block">
        <h3 className="panel-h admin-h">
          SPEND · {budget.period}
          <span className={`chip ${budgetTone}`}>{pct(budget.fraction)} of cap</span>
        </h3>
        <dl className="kv">
          <span className="kv-pair"><dt>Spent</dt><dd className="mono">{fmtUsd(budget.spentUsd.toFixed(2))}</dd></span>
          <span className="kv-pair"><dt>Cap</dt><dd className="mono">{fmtUsd(budget.capUsd.toFixed(2))}</dd></span>
          <span className="kv-pair"><dt>Model calls</dt><dd className="mono">{budget.callCount}</dd></span>
          {/* The tier the budget has degraded to. It steps down as the cap
              approaches rather than stopping dead, so a month that runs hot
              still publishes — at a cheaper model. */}
          <span className="kv-pair"><dt>Tier</dt><dd className="mono">{budget.tier}</dd></span>
        </dl>
      </section>

      <section className="admin-block">
        <h3 className="panel-h">OUTPUT QUALITY</h3>
        <p className="panel-body dim">
          A climb in the rejection rate is a prompt regression — the model is
          producing annotations the validation layer throws away, and a user
          sees fewer of them without anything erroring.
        </p>
        <dl className="kv">
          <span className="kv-pair">
            <dt>Rejection rate</dt>
            <dd className={`mono ${audit.rejectionRate > 0.2 ? 'bad' : ''}`}>
              {pct(audit.rejectionRate)}
            </dd>
          </span>
          <span className="kv-pair"><dt>Accepted</dt><dd className="mono">{audit.annotationsAccepted}</dd></span>
          <span className="kv-pair"><dt>Rejected</dt><dd className="mono">{audit.annotationsRejected}</dd></span>
          <span className="kv-pair"><dt>Errors</dt><dd className="mono">{audit.errors}</dd></span>
          {/* Zero across repeated requests means a silent cache invalidator is
              present, and every one of those is a full-price model call. */}
          <span className="kv-pair"><dt>Cache hit rate</dt><dd className="mono">{pct(audit.cacheHitRate)}</dd></span>
          <span className="kv-pair">
            <dt>First token p50 / p95</dt>
            <dd className="mono">
              {audit.p50FirstTokenMs ?? '—'} / {audit.p95FirstTokenMs ?? '—'} ms
            </dd>
          </span>
        </dl>
      </section>

      <section className="admin-block">
        <h3 className="panel-h">
          UNIVERSE · {data.universe.length} SYMBOL{data.universe.length === 1 ? '' : 'S'}
        </h3>
        <p className="panel-body dim">
          What is actually being analysed, which with demand gating is not the
          declared list. See ADR-0011.
        </p>
        <p className="mono admin-universe">{data.universe.join('  ') || '—'}</p>
        {data.demand ? (
          <dl className="kv">
            {/* `tracked` counts symbols with a live demand entry; `watched` is
                the subset somebody is looking at right now. The gap between
                them is how much of the universe is being analysed on momentum
                rather than on demand. */}
            <span className="kv-pair"><dt>Tracked</dt><dd className="mono">{data.demand.tracked}</dd></span>
            <span className="kv-pair"><dt>Watched now</dt><dd className="mono">{data.demand.watched}</dd></span>
            <span className="kv-pair"><dt>Viewers</dt><dd className="mono">{data.demand.viewers}</dd></span>
          </dl>
        ) : null}
      </section>

      {Object.keys(audit.costBySymbol).length > 0 ? (
        <section className="admin-block">
          <h3 className="panel-h">COST BY SYMBOL</h3>
          <ul className="admin-list">
            {Object.entries(audit.costBySymbol)
              .sort((a, b) => b[1] - a[1])
              .map(([symbol, cost]) => (
                <li key={symbol} className="admin-row admin-row-2">
                  <span className="admin-name">{symbol}</span>
                  <span className="mono">{fmtUsd(cost.toFixed(4))}</span>
                </li>
              ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
